/**
 * ============================================================================
 * useVenueData - PRODUCTION-READY VENUE DATA HOOKS
 * ============================================================================
 *
 * TanStack Query hooks for Venue Information with:
 * - Smart caching (venue data cached for 5min, rarely changes)
 * - Separate mutations for different operations
 * - Proper error handling and loading states
 * - NO polling - venue data is mostly static
 *
 * CACHING STRATEGY:
 * - Venue details: 5min cache (changes rarely)
 * - Operating hours: Managed within venue data
 * - Sports & Amenities: Managed within venue data
 * - Mutations invalidate relevant queries automatically
 *
 * ============================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import vendorApi, { type Venue } from '../services/api';
import type { OperatingHours } from '../types';

// ============================================================================
// TYPES
// ============================================================================

export interface VenueDataResponse {
  venue: Venue;
  courts: any[];
  blackoutDates: any[];
}

export interface UpdateOperatingHoursData {
  venueId: number;
  hours: OperatingHours[];
}

export interface UpdateSportRatesData {
  venueId: number;
  rates: Record<number, {
    rate?: string;
    pricing_type?: 'hourly' | 'per_person';
  }>;
}

export interface UploadImagesData {
  venueId: number;
  images: File[];
}

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Fetch complete venue data including details, courts, and blackout dates
 */
export function useVenueData(userId: number | undefined, venueId: number | undefined) {
  return useQuery<VenueDataResponse, Error>({
    queryKey: ['venue-info', userId, venueId],

    queryFn: async () => {
      if (!userId || !venueId) throw new Error('User ID and Venue ID required');

      // Fetch dashboard data (includes venue details)
      const dashboardData = await vendorApi.getDashboard();

      if (!dashboardData.venues || dashboardData.venues.length === 0) {
        throw new Error('No venue found for this user');
      }

      const venue = dashboardData.venues[0];

      // Fetch additional data in parallel
      const [blackoutDatesData, courtsData] = await Promise.all([
        vendorApi.getBlackoutDates(venue.id).catch(() => []),
        vendorApi.getCourts(venue.id).catch(() => []),
      ]);

      return {
        venue,
        blackoutDates: Array.isArray(blackoutDatesData)
          ? blackoutDatesData
          : ((blackoutDatesData as any)?.data || []),
        courts: courtsData,
      };
    },

    enabled: !!userId && !!venueId,
    staleTime: 5 * 60 * 1000, // 5 minutes - venue info changes rarely
    gcTime: 10 * 60 * 1000, // 10 minutes in cache
    refetchOnWindowFocus: false,
    refetchOnMount: true, // Always fetch fresh on mount
  });
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Update operating hours
 * TODO: Add when editing functionality is implemented
 */
export function useUpdateOperatingHours() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ venueId, hours }: UpdateOperatingHoursData) => {
      // Transform OperatingHours to API format
      const transformedHours = hours.map(h => ({
        day_of_week: h.day,
        is_closed: !h.isOpen,
        opening_time: h.isOpen ? h.openTime : null,
        closing_time: h.isOpen ? h.closeTime : null,
      }));

      return await vendorApi.updateOperatingHours(venueId, transformedHours);
    },

    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['venue-info'] });
    },

    onError: (error: any) => {
      console.error('Failed to update operating hours:', error);
      throw error;
    },
  });
}
